import { useState, useMemo, useEffect, useSyncExternalStore } from "react";
import { Panel } from "@xyflow/react";
import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddOutlined from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ExpandLessOutlined from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlined from "@mui/icons-material/ExpandMoreOutlined";
import ExtensionOutlined from "@mui/icons-material/ExtensionOutlined";
import MoreVert from "@mui/icons-material/MoreVert";
import ModalEquipamentoPersonalizado from "../customEquipmentTypes/ModalEquipamentoPersonalizado";
import { deleteCustomEquipmentType } from "../customEquipmentTypes/customEquipmentTypesService";
import {
  getCustomEquipmentTypesSnapshot,
  refreshCustomEquipmentTypes,
  subscribeCustomEquipmentTypes,
} from "../customEquipmentTypes/customEquipmentTypesStore";
import InternoModalConfirmacaoSaida from "../../../../../../../components/modalSaindoDaPagina";
import styles from "./styles";

// Prefixo das preferências da paleta salvas no localStorage
const PALETTE_STORAGE_PREFIX = "diagrama-equipamentos-palette-";

// Lê preferência de abertura da paleta no localStorage de forma segura
const carregarPreferenciaPaleta = (editMode) => {
  const padrao = !!editMode;
  try {
    const raw = window?.localStorage?.getItem?.(
      `${PALETTE_STORAGE_PREFIX}${editMode ? "edicao" : "visualizacao"}`,
    );
    const parsed = raw ? JSON.parse(raw) : null;
    return typeof parsed === "boolean" ? parsed : padrao;
  } catch {
    return padrao;
  }
};

// Salva preferência de abertura da paleta no localStorage sem interromper execução
const salvarPreferenciaPaleta = (editMode, expandida) => {
  try {
    window?.localStorage?.setItem?.(
      `${PALETTE_STORAGE_PREFIX}${editMode ? "edicao" : "visualizacao"}`,
      JSON.stringify(!!expandida),
    );
  } catch {
    // Ignora indisponibilidade do localStorage
  }
};

// Componente da paleta de tipos de equipamento com busca e suporte a tipos personalizados
export default function PaletaEquipamentos({
  equipmentTypes = [],
  editMode = false,
  onAdicionarNode = () => {},
  setSnackbar = () => {},
}) {
  // Controle de expansão do painel da paleta
  const [paletteExpanded, setPaletteExpanded] = useState(() =>
    carregarPreferenciaPaleta(editMode),
  );

  // Termo digitado no campo de busca da paleta
  const [paletteSearch, setPaletteSearch] = useState("");

  // Modais de criação/edição de tipo de equipamento personalizado
  const [customTypeModalOpen, setCustomTypeModalOpen] = useState(false);
  const [customTypeModalMode, setCustomTypeModalMode] = useState("create");
  const [customTypeModalData, setCustomTypeModalData] = useState(null);

  // Menu de contexto e alvo de ações de tipo personalizado
  const [customTypeMenuAnchor, setCustomTypeMenuAnchor] = useState(null);
  const [customTypeMenuTarget, setCustomTypeMenuTarget] = useState(null);
  const [customTypeDeleteConfirm, setCustomTypeDeleteConfirm] = useState(null);

  // Tema atual da aplicação
  const theme = useTheme();

  // Estilos da paleta baseados no tema
  const paletteStyles = styles(theme);

  // Catálogo de tipos personalizados obtido da store reativa
  const customEquipmentTypes = useSyncExternalStore(
    subscribeCustomEquipmentTypes,
    getCustomEquipmentTypesSnapshot,
  );

  // Sincroniza estado de expansão ao alternar entre modo de edição e visualização
  useEffect(() => {
    setPaletteExpanded(carregarPreferenciaPaleta(editMode));
  }, [editMode]);

  // Filtra equipamentos pelo termo digitado na busca
  const equipmentTypesFiltrados = useMemo(() => {
    const termo = paletteSearch?.trim()?.toLowerCase();
    if (!termo) return equipmentTypes || [];
    return (equipmentTypes || []).filter((tipo) =>
      tipo?.label?.toLowerCase()?.includes(termo),
    );
  }, [equipmentTypes, paletteSearch]);

  // Exclui um tipo de equipamento personalizado e atualiza o catálogo
  const handleDeleteCustomType = async () => {
    const alvo = customTypeDeleteConfirm;
    if (!alvo?.id) return;
    try {
      await deleteCustomEquipmentType(alvo.id);
      setSnackbar?.({
        children: "Equipamento personalizado excluído.",
        severity: "success",
      });
      refreshCustomEquipmentTypes();
    } catch (error) {
      console.error("Erro ao excluir equipamento personalizado:", error);
      setSnackbar?.({
        children: "Erro ao excluir equipamento personalizado.",
        severity: "error",
      });
    } finally {
      setCustomTypeDeleteConfirm(null);
    }
  };

  // Alterna e persiste a expansão da paleta
  const togglePalette = () => {
    setPaletteExpanded((prev) => {
      const next = !prev;
      salvarPreferenciaPaleta(editMode, next);
      if (!next) setPaletteSearch("");
      return next;
    });
  };

  return (
    <>
      <Panel position="top-left">
        <Box sx={paletteStyles.paletteBox}>
          <Box sx={paletteStyles.paletteHeader}>
            <Typography sx={paletteStyles.paletteTitle}>
              Equipamentos
            </Typography>
            <Tooltip
              title={
                paletteExpanded
                  ? "Recolher equipamentos"
                  : "Expandir equipamentos"
              }
              arrow
            >
              <IconButton
                size="small"
                sx={paletteStyles.paletteToggleButton}
                onClick={togglePalette}
                aria-label={
                  paletteExpanded
                    ? "Recolher equipamentos"
                    : "Expandir equipamentos"
                }
              >
                {paletteExpanded ? (
                  <ExpandLessOutlined fontSize="small" />
                ) : (
                  <ExpandMoreOutlined fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          {paletteExpanded && (
            <>
              {/* Campo de busca para filtrar equipamentos por nome na paleta */}
              <TextField
                size="small"
                placeholder="Buscar equipamento..."
                value={paletteSearch}
                onChange={(evento) =>
                  setPaletteSearch(evento?.target?.value || "")
                }
                autoComplete="off"
                sx={paletteStyles.paletteSearch}
                inputProps={{ "aria-label": "Buscar equipamento" }}
              />
              <Box sx={paletteStyles.paletteGrid}>
                {(equipmentTypesFiltrados || []).map((tipo) => {
                  const Icone = tipo?.icon;
                  const isCustom = !!tipo?.isCustom;
                  const cardButton = (
                    <Box
                      component="button"
                      type="button"
                      sx={{
                        ...paletteStyles.paletteButton,
                        ...(!editMode
                          ? paletteStyles.paletteButtonDisabled
                          : {}),
                      }}
                      onClick={() => onAdicionarNode?.(tipo)}
                    >
                      {isCustom ? (
                        <Avatar
                          src={tipo?.iconUrl}
                          variant="rounded"
                          sx={paletteStyles.paletteButtonAvatar}
                        >
                          <ExtensionOutlined
                            sx={paletteStyles.paletteButtonIcon}
                          />
                        </Avatar>
                      ) : Icone ? (
                        <Icone sx={paletteStyles.paletteButtonIcon} />
                      ) : null}
                      <Typography sx={paletteStyles.paletteButtonLabel}>
                        {tipo?.label}
                      </Typography>
                    </Box>
                  );

                  return (
                    <Tooltip
                      key={tipo?.id}
                      title={
                        editMode
                          ? `Adicionar ${tipo?.label}`
                          : "Ative o modo de edição"
                      }
                      arrow
                    >
                      {isCustom ? (
                        <Box sx={paletteStyles.paletteItemWrapper}>
                          {cardButton}
                          {editMode && (
                            <IconButton
                              size="small"
                              sx={paletteStyles.paletteItemMenuButton}
                              onClick={(evento) => {
                                evento?.stopPropagation?.();
                                setCustomTypeMenuAnchor(
                                  evento?.currentTarget || null,
                                );
                                setCustomTypeMenuTarget(tipo);
                              }}
                              aria-label={`Mais ações de ${tipo?.label}`}
                            >
                              <MoreVert sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                        </Box>
                      ) : (
                        cardButton
                      )}
                    </Tooltip>
                  );
                })}
                {/* Botão de criação de tipo personalizado oculto durante busca */}
                {!paletteSearch && (
                  <Tooltip
                    title={
                      editMode
                        ? "Criar equipamento personalizado"
                        : "Ative o modo de edição"
                    }
                    arrow
                  >
                    <Box
                      component="button"
                      type="button"
                      sx={{
                        ...paletteStyles.paletteButton,
                        ...paletteStyles.paletteButtonAdd,
                        ...(!editMode
                          ? paletteStyles.paletteButtonDisabled
                          : {}),
                      }}
                      onClick={() => {
                        if (!editMode) return;
                        setCustomTypeModalMode("create");
                        setCustomTypeModalData(null);
                        setCustomTypeModalOpen(true);
                      }}
                    >
                      <AddOutlined sx={paletteStyles.paletteButtonIcon} />
                      <Typography sx={paletteStyles.paletteButtonLabel}>
                        Personalizado
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </>
          )}
        </Box>
      </Panel>

      {/* Menu de ações do card de tipo personalizado */}
      <Menu
        anchorEl={customTypeMenuAnchor}
        open={Boolean(customTypeMenuAnchor)}
        onClose={() => {
          setCustomTypeMenuAnchor(null);
          setCustomTypeMenuTarget(null);
        }}
      >
        <MenuItem
          onClick={() => {
            setCustomTypeModalMode("update");
            setCustomTypeModalData(
              customEquipmentTypes?.find(
                (item) => item?.id === customTypeMenuTarget?.id,
              ) || null,
            );
            setCustomTypeModalOpen(true);
            setCustomTypeMenuAnchor(null);
          }}
        >
          <EditOutlined fontSize="small" sx={{ mr: 1 }} /> Editar
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCustomTypeDeleteConfirm(customTypeMenuTarget);
            setCustomTypeMenuAnchor(null);
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteOutlineOutlined fontSize="small" sx={{ mr: 1 }} /> Excluir
        </MenuItem>
      </Menu>

      {/* Modal de criação e edição de equipamento personalizado */}
      <ModalEquipamentoPersonalizado
        open={customTypeModalOpen}
        onClose={() => setCustomTypeModalOpen(false)}
        onSaved={() => {
          refreshCustomEquipmentTypes();
          setSnackbar?.({
            children:
              customTypeModalMode === "create"
                ? "Equipamento personalizado criado."
                : "Equipamento personalizado atualizado.",
            severity: "success",
          });
        }}
        mode={customTypeModalMode}
        initialData={customTypeModalData}
        setSnackbar={setSnackbar}
      />

      {/* Modal de confirmação de exclusão de tipo personalizado */}
      <InternoModalConfirmacaoSaida
        openConfirmacao={Boolean(customTypeDeleteConfirm)}
        setOpenConfirmacao={(open) => {
          if (!open) setCustomTypeDeleteConfirm(null);
        }}
        title="Excluir equipamento personalizado?"
        headerText={`Tem certeza que deseja excluir "${customTypeDeleteConfirm?.label || ""}"?`}
        contentText="Equipamentos que já usam esse tipo em algum diagrama manterão o título, mas passarão a exibir o ícone padrão."
        confirmText="Excluir"
        execute={handleDeleteCustomType}
        cancel={() => setCustomTypeDeleteConfirm(null)}
        severity="error"
      />
    </>
  );
}
